import { useState, useCallback, useEffect } from "react";
import type { OrgChartViewState } from "@/types";

const STORAGE_KEY = "org-chart-view-state";
const DEFAULT_STATE: OrgChartViewState = {
  zoom: 1,
  pan: { x: 0, y: 0 },
  collapsedNodes: [],
  focusedNode: null,
};

/**
 * Hook to manage org chart view state (zoom, pan, collapsed nodes)
 * Persists state to sessionStorage for current session
 * 
 * @returns Object containing view state and state management functions
 */
export function useOrgChartState() {
  // Initialize from sessionStorage if available
  const [state, setState] = useState<OrgChartViewState>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Failed to load org chart state from sessionStorage:", error);
    }
    return DEFAULT_STATE;
  });

  // Persist to sessionStorage whenever state changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error("Failed to save org chart state to sessionStorage:", error);
    }
  }, [state]);

  // Zoom functions
  const setZoom = useCallback((zoom: number) => {
    const clampedZoom = Math.max(0.5, Math.min(2, zoom));
    setState((prev) => ({ ...prev, zoom: clampedZoom }));
  }, []);

  const zoomIn = useCallback(() => {
    setState((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom + 0.25, 2),
    }));
  }, []);

  const zoomOut = useCallback(() => {
    setState((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom - 0.25, 0.5),
    }));
  }, []);

  // Pan functions
  const setPan = useCallback((pan: { x: number; y: number }) => {
    setState((prev) => ({ ...prev, pan }));
  }, []);

  // Collapse/expand functions
  const toggleCollapse = useCallback((nodePid: string) => {
    setState((prev) => {
      const isCollapsed = prev.collapsedNodes.includes(nodePid);
      const collapsedNodes = isCollapsed
        ? prev.collapsedNodes.filter((pid) => pid !== nodePid)
        : [...prev.collapsedNodes, nodePid];
      return { ...prev, collapsedNodes };
    });
  }, []);

  const collapseNode = useCallback((nodePid: string) => {
    setState((prev) => {
      if (prev.collapsedNodes.includes(nodePid)) return prev;
      return {
        ...prev,
        collapsedNodes: [...prev.collapsedNodes, nodePid],
      };
    });
  }, []);

  const expandNode = useCallback((nodePid: string) => {
    setState((prev) => ({
      ...prev,
      collapsedNodes: prev.collapsedNodes.filter((pid) => pid !== nodePid),
    }));
  }, []);

  const expandAll = useCallback(() => {
    setState((prev) => ({ ...prev, collapsedNodes: [] }));
  }, []);

  // Focus functions
  const setFocusedNode = useCallback((nodePid: string | null) => {
    setState((prev) => ({ ...prev, focusedNode: nodePid }));
  }, []);

  // Reset to default state
  const resetView = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);

  return {
    // State
    zoom: state.zoom,
    pan: state.pan,
    collapsedNodes: state.collapsedNodes,
    focusedNode: state.focusedNode,

    // Zoom controls
    setZoom,
    zoomIn,
    zoomOut,

    // Pan controls
    setPan,

    // Collapse controls
    toggleCollapse,
    collapseNode,
    expandNode,
    expandAll,

    // Focus controls
    setFocusedNode,

    // Reset
    resetView,
  };
}
